// @ts-nocheck
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Popover,
  useTheme,
  alpha,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import ColorLensIcon from '@mui/icons-material/ColorLens';

import ColorPicker from '../FontStyles/ColorPicker';

const StyleEditor = ({ rule, onStyleChange }) => {
  const [colorAnchorEl, setColorAnchorEl] = useState(null);
  const theme = useTheme();

  const handleStyleToggle = useCallback(
    (style, value) => {
      onStyleChange({
        ...rule.styles,
        [style]: value,
      });
    },
    [rule?.styles, onStyleChange]
  );

  const handleFormatChange = useCallback(
    (_event, formats) => {
      const newStyles = { ...rule.styles };

      if (formats.includes('bold')) {
        newStyles.fontWeight = 'bold';
      } else {
        newStyles.fontWeight = 'normal';
      }

      if (formats.includes('italic')) {
        newStyles.fontStyle = 'italic';
      } else {
        newStyles.fontStyle = 'normal';
      }

      if (formats.includes('underline')) {
        newStyles.textDecoration = 'underline';
      } else {
        newStyles.textDecoration = 'none';
      }

      onStyleChange(newStyles);
    },
    [rule.styles, onStyleChange]
  );

  const getCurrentFormats = useCallback(() => {
    const formats = [];
    if (rule.styles.fontWeight === 'bold') {
      formats.push('bold');
    }
    if (rule.styles.fontStyle === 'italic') {
      formats.push('italic');
    }
    if (rule.styles.textDecoration === 'underline') {
      formats.push('underline');
    }
    return formats;
  }, [rule.styles]);

  const handleColorClick = useCallback(
    (event) => {
      setColorAnchorEl(event.currentTarget);
    },
    [setColorAnchorEl]
  );

  const handleColorClose = useCallback(() => {
    setColorAnchorEl(null);
  }, [setColorAnchorEl]);

  const handleColorSelect = useCallback(
    (color) => {
      handleStyleToggle('backgroundColor', color);
      handleColorClose();
    },
    [handleStyleToggle, handleColorClose]
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <ToggleButton
        value="color"
        onClick={handleColorClick}
        sx={{
          borderColor: alpha(theme?.palette?.primary?.main, 0.1),
          backgroundColor: rule.styles.backgroundColor,
          width: '32px',
          height: '32px',
        }}
      >
        <ColorLensIcon fontSize="small" />
      </ToggleButton>

      <Popover
        open={Boolean(colorAnchorEl)}
        anchorEl={colorAnchorEl}
        onClose={handleColorClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{ zIndex: 2300 }}
      >
        <ColorPicker
          color={rule.styles.backgroundColor}
          onColorSelect={handleColorSelect}
        />
      </Popover>

      <ToggleButtonGroup
        size="small"
        value={getCurrentFormats()}
        onChange={handleFormatChange}
      >
        <ToggleButton value="bold">
          <Tooltip title="Bold">
            <FormatBoldIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="italic">
          <Tooltip title="Italic">
            <FormatItalicIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="underline">
          <Tooltip title="Underline">
            <FormatUnderlinedIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

StyleEditor.propTypes = {
  rule: PropTypes.shape({
    styles: PropTypes.shape({
      backgroundColor: PropTypes.string,
      fontWeight: PropTypes.string,
      fontStyle: PropTypes.string,
      textDecoration: PropTypes.string,
    }).isRequired,
  }).isRequired,
  onStyleChange: PropTypes.func.isRequired,
};

export default StyleEditor;
